import { useEffect, useState } from 'react';
import { ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

type UseNumericFieldProps = {
  field: ControllerRenderProps<FieldValues, string>;
  fieldName: string;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
};

type UseNumericFieldReturn = {
  localValue: string;
  updateValue: (newValue: number | string) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleIncrement: () => void;
  handleDecrement: () => void;
};

export const useNumericField = ({ field, fieldName, min, max, step, disabled }: UseNumericFieldProps): UseNumericFieldReturn => {
  const { setValue } = useFormContext();
  const [localValue, setLocalValue] = useState(field.value ?? '');
  const [debouncedValue] = useDebounceValue(localValue, 300);

  useEffect(() => {
    if (debouncedValue !== '' && !Number.isNaN(Number(debouncedValue))) {
      setValue(fieldName, Number(debouncedValue), {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [debouncedValue, setValue, fieldName]);

  const updateValue = (newValue: number | string): void => {
    const stringValue = String(newValue);
    setLocalValue(stringValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = Number.parseInt(e.target.value, 10);
    if (Number.isNaN(value) || e.target.value === '') {
      updateValue('');
    } else {
      let finalValue = value;
      if (value < min) {
        finalValue = min;
      } else if (value > max) {
        finalValue = max;
      }
      updateValue(finalValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    field.onBlur();
    const value = Number.parseInt(e.target.value, 10);
    if (Number.isNaN(value) || e.target.value === '') {
      updateValue(min);
      setValue(fieldName, min, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleIncrement = (): void => {
    if (!disabled) {
      const currentValue = Number(localValue) || 0;
      const newValue = Math.min(currentValue + step, max);
      updateValue(newValue);
    }
  };

  const handleDecrement = (): void => {
    if (!disabled) {
      const currentValue = Number(localValue) || 0;
      const newValue = Math.max(currentValue - step, min);
      updateValue(newValue);
    }
  };

  return {
    localValue,
    updateValue,
    handleChange,
    handleBlur,
    handleIncrement,
    handleDecrement,
  };
};
